// src\pages\PortfolioDetail.jsx
// Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePortfolio } from '../hooks/usePortfolio';
import {
  FiArrowLeft,
  FiCalendar,
  FiTag,
  FiUser,
  FiHeart,
  FiEye,
  FiShare2,
  FiMousePointer,
  FiImage,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiStar,
  FiArrowRight,
  FiGrid,
  FiClock,
  FiBriefcase,
  FiFolder,
  FiExternalLink,
  FiTrendingUp,
  FiZap,
  FiAward,
  FiX,
  FiCopy,
  FiCheckCircle,
  FiTwitter,
  FiFacebook,
  FiLinkedin,
  FiInstagram,
  FiSend,
  FiMail,
  FiLink,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import PortfolioDetailSkeleton from '../components/portfolio/PortfolioDetailSkeleton';
import { FiverrCard } from '../components/FiverrCard';
import { motion } from 'framer-motion';
import ImagePreviewModal from '../components/portfolio/ImagePreviewModal';
// ✅ Import simplified ogImageService for share modal ONLY
import { ogImageService } from '../services/ogImageService';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

// =============================================
// SOCIAL SHARE MODAL - Uses simplified ogImageService
// =============================================
const SocialShareModal = ({ isOpen, onClose, project, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${shareUrl}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'portfolio_project',
          content_id: project?.id,
          content_title: project?.title,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  // ✅ Use simplified ogImageService for modal preview ONLY
  const shareData = useMemo(() => {
    if (!project) return null;
    return ogImageService.portfolioProject(project);
  }, [project]);

  if (!isOpen || !project || !shareData) return null;

  const sharePlatforms = [
    {
      id: 'twitter',
      name: 'Twitter',
      icon: FiTwitter,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(project.title)}&url=${encodeURIComponent(shareUrl)}&via=wdhejbul`,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: FiFacebook,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(project.title)}`,
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: FiLinkedin,
      color: '#0A66C2',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: FaWhatsapp,
      color: '#25D366',
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`📝 ${project.title}\n\n${shareUrl}`)}`,
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: FiSend,
      color: '#26A5E4',
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`📝 ${project.title}`)}`,
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: FiInstagram,
      color: '#E4405F',
      url: null,
      action: () => {
        copyToClipboard();
        alert('📸 Instagram: Link copied! Open Instagram app to share.');
        trackShare('instagram');
      },
    },
    {
      id: 'email',
      name: 'Email',
      icon: FiMail,
      color: '#EA4335',
      url: `mailto:?subject=${encodeURIComponent(`Check out: ${project.title}`)}&body=${encodeURIComponent(`I thought you might find this interesting:\n\n${project.title}\n${shareUrl}`)}`,
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{
          backgroundColor: isDark
            ? 'rgba(20,20,20,0.98)'
            : 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          border: isDark
            ? '1px solid rgba(255,255,255,0.08)'
            : '1px solid rgba(0,0,0,0.06)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 px-6 py-4 border-b flex items-center justify-between"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            backgroundColor: isDark
              ? 'rgba(20,20,20,0.95)'
              : 'rgba(255,255,255,0.95)',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: isDark
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.05)',
              }}
            >
              <FiShare2 className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
            <div>
              <h3
                className="text-lg font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Share this project
              </h3>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Spread the word about "{project.title}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Preview Card - Uses simplified shareData */}
        <div className="px-6 pt-4">
          <div
            className="rounded-xl overflow-hidden border"
            style={{
              borderColor: isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)',
            }}
          >
            <div className="relative aspect-[1.91/1] bg-gray-100 dark:bg-gray-800">
              <img
                src={shareData.image || DEFAULT_IMAGE}
                alt={project.title}
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.src = DEFAULT_IMAGE;
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                <p className="text-white font-semibold text-lg line-clamp-1">
                  {shareData.title || project.title}
                </p>
                <p className="text-white/70 text-sm line-clamp-1">
                  {shareData.description || project.description}
                </p>
                <p className="text-white/40 text-xs mt-0.5">{shareUrl}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Share Buttons Grid */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {sharePlatforms.map(platform => {
              const Icon = platform.icon;
              const isDisabled = !platform.url && !platform.action;

              return (
                <button
                  key={platform.id}
                  onClick={() => {
                    if (platform.action) {
                      platform.action();
                    } else if (platform.url) {
                      window.open(
                        platform.url,
                        '_blank',
                        'noopener,noreferrer,width=600,height=400',
                      );
                      trackShare(platform.id);
                    }
                  }}
                  disabled={isDisabled}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 ${
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:scale-105 hover:shadow-md active:scale-95'
                  }`}
                  style={{
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.03)'
                      : 'rgba(0,0,0,0.02)',
                    border: isDark
                      ? '1px solid rgba(255,255,255,0.06)'
                      : '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${platform.color}15`,
                      color: platform.color,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-xs font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    {platform.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Copy Link Section */}
        <div
          className="px-6 pb-6 border-t"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <div className="pt-4">
            <div
              className="flex items-center gap-2 p-2 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <FiLink
                className="w-4 h-4 flex-shrink-0"
                style={{ color: 'var(--muted)' }}
              />
              <p
                className="text-xs truncate flex-1"
                style={{ color: 'var(--muted)' }}
              >
                {shareUrl}
              </p>
              <button
                onClick={copyToClipboard}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  copied
                    ? 'text-green-500 bg-green-500/10'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ color: copied ? '#22c55e' : 'var(--text)' }}
              >
                {copied ? (
                  <>
                    <FiCheckCircle className="w-3.5 h-3.5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <FiCopy className="w-3.5 h-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// =============================================
// SHARE BUTTONS - Updated with Modal
// =============================================
const ShareButtons = ({ url, title, description, imageUrl, project }) => {
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${url}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'portfolio_project',
          content_id: project?.id,
          content_title: title,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  // Quick share functions
  const quickShare = platform => {
    const encodedTitle = encodeURIComponent(`📝 ${title}`);
    const encodedUrl = encodeURIComponent(url);

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&via=wdhejbul`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%0A${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      instagram: null,
      email: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A%0A${encodedUrl}`,
    };

    if (platform === 'instagram') {
      copyToClipboard();
      alert('📸 Instagram: Link copied! Open Instagram app to share.');
      trackShare('instagram');
      return;
    }

    const shareUrl = urls[platform];
    if (shareUrl) {
      window.open(
        shareUrl,
        '_blank',
        'noopener,noreferrer,width=600,height=400',
      );
      trackShare(platform);
    }
  };

  const quickPlatforms = [
    { id: 'twitter', icon: FiTwitter, color: '#1DA1F2' },
    { id: 'facebook', icon: FiFacebook, color: '#1877F2' },
    { id: 'linkedin', icon: FiLinkedin, color: '#0A66C2' },
    { id: 'whatsapp', icon: FaWhatsapp, color: '#25D366' },
    { id: 'telegram', icon: FiSend, color: '#26A5E4' },
  ];

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          onClick={copyToClipboard}
          className={`p-1.5 rounded-lg transition-all duration-200 cursor-pointer ${
            copied
              ? 'text-green-500 bg-green-500/10'
              : 'hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{ color: copied ? '#22c55e' : 'var(--text)' }}
          aria-label="Copy link"
          title="Copy link"
        >
          {copied ? (
            <FiCheckCircle className="w-4 h-4" />
          ) : (
            <FiCopy className="w-4 h-4" />
          )}
        </button>

        {quickPlatforms.map(platform => {
          const Icon = platform.icon;
          return (
            <button
              key={platform.id}
              onClick={() => quickShare(platform.id)}
              className="p-1.5 rounded-lg transition-all duration-200 cursor-pointer hover:scale-110 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
              aria-label={`Share on ${platform.id}`}
              title={`Share on ${platform.id}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}

        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer text-sm"
          style={{ color: 'var(--text)' }}
          aria-label="More share options"
          title="More share options"
        >
          <FiShare2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </button>
      </div>

      {showModal && (
        <SocialShareModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          project={project}
          shareUrl={url}
        />
      )}
    </>
  );
};

// =============================================
// FORMAT DESCRIPTION
// =============================================
const formatDescription = text => {
  if (!text) return null;

  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());

  return paragraphs.map((paragraph, idx) => {
    if (paragraph.trim().match(/^[-•*]\s/)) {
      const items = paragraph.split(/\n/).filter(line => line.trim());
      return (
        <ul key={idx} className="space-y-2 my-4">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">•</span>
              <span
                style={{ color: 'var(--text)' }}
                className="leading-relaxed"
              >
                {item.replace(/^[-•*]\s/, '')}
              </span>
            </li>
          ))}
        </ul>
      );
    }

    if (paragraph.trim().match(/^##\s/)) {
      const headingText = paragraph.replace(/^##\s/, '');
      return (
        <h3
          key={idx}
          className="text-xl font-bold mt-6 mb-3"
          style={{ color: 'var(--text)' }}
        >
          {headingText}
        </h3>
      );
    }

    if (paragraph.trim().match(/^###\s/)) {
      const headingText = paragraph.replace(/^###\s/, '');
      return (
        <h4
          key={idx}
          className="text-lg font-semibold mt-5 mb-2"
          style={{ color: 'var(--text)' }}
        >
          {headingText}
        </h4>
      );
    }

    if (paragraph.trim().match(/^\d+\.\s/)) {
      const items = paragraph.split(/\n/).filter(line => line.trim());
      return (
        <ol key={idx} className="space-y-2 my-4 list-decimal list-inside">
          {items.map((item, i) => (
            <li
              key={i}
              className="leading-relaxed"
              style={{ color: 'var(--text)' }}
            >
              {item.replace(/^\d+\.\s/, '')}
            </li>
          ))}
        </ol>
      );
    }

    return (
      <p
        key={idx}
        className="leading-relaxed mb-4"
        style={{ color: 'var(--text)' }}
      >
        {paragraph}
      </p>
    );
  });
};

// =============================================
// MAIN COMPONENT
// =============================================
function PortfolioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { getProject, trackView, trackClick, projects, likeProject } =
    usePortfolio();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState({});

  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isLiking, setIsLiking] = useState(false);

  const [suggestedProjects, setSuggestedProjects] = useState([]);
  const [popularProjects, setPopularProjects] = useState([]);
  const [forYouProjects, setForYouProjects] = useState([]);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  const hasTrackedRef = useRef(false);

  // ✅ REMOVED: metaData - No client-side OG generation
  // Social media scrapers get OG tags from server API

  // Load project
  useEffect(() => {
    const loadProject = async () => {
      if (!id) {
        setError('No project ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getProject(id);
        if (data) {
          setProject(data);
          setLikesCount(data.likes || 0);

          const likedProjects = JSON.parse(
            localStorage.getItem('likedProjects') || '[]',
          );
          setLiked(likedProjects.includes(data.id));

          if (projects && projects.length > 0) {
            const suggested = projects
              .filter(
                p =>
                  p.id !== data.id &&
                  (p.category === data.category || p.type === data.type),
              )
              .slice(0, 6);
            setSuggestedProjects(suggested);

            const popular = [...projects]
              .filter(p => p.id !== data.id)
              .sort((a, b) => (b.likes || 0) - (a.likes || 0))
              .slice(0, 4);
            setPopularProjects(popular);

            const shuffled = [...projects]
              .filter(p => p.id !== data.id)
              .sort(() => Math.random() - 0.5)
              .slice(0, 4);
            setForYouProjects(shuffled);
          }
        } else {
          setError('Project not found');
        }
      } catch (err) {
        console.error('Error loading project:', err);
        setError(err.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id, getProject, projects]);

  // Track view and click
  useEffect(() => {
    if (project && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      trackView(project.id).catch(err =>
        console.error('Error tracking view:', err),
      );
      trackClick(project.id).catch(err =>
        console.error('Error tracking click:', err),
      );
    }
  }, [project, trackView, trackClick]);

  // Handle like
  const handleLike = async () => {
    if (isLiking || !project) return;

    const wasLiked = liked;
    const previousLikesCount = likesCount;

    const newLikedState = !liked;
    const newLikesCount = newLikedState ? likesCount + 1 : likesCount - 1;

    setLiked(newLikedState);
    setLikesCount(newLikesCount);
    setIsLiking(true);

    const likedProjects = JSON.parse(
      localStorage.getItem('likedProjects') || '[]',
    );
    if (newLikedState) {
      if (!likedProjects.includes(project.id)) likedProjects.push(project.id);
    } else {
      const index = likedProjects.indexOf(project.id);
      if (index > -1) likedProjects.splice(index, 1);
    }
    localStorage.setItem('likedProjects', JSON.stringify(likedProjects));

    try {
      const result = await likeProject(project.id, wasLiked);
      if (result && result.likes !== undefined) setLikesCount(result.likes);
    } catch (error) {
      console.error('Error liking project:', error);
      setLiked(wasLiked);
      setLikesCount(previousLikesCount);
    } finally {
      setIsLiking(false);
    }
  };

  // Get optimized image URL
  const getOptimizedImageUrl = (url, width = 800) => {
    if (!url) return '';
    if (url.includes('cloudinary.com')) {
      return url.replace(
        '/upload/',
        `/upload/w_${width},q_auto:best,f_auto,c_limit,g_center,dpr_auto/`,
      );
    }
    return url;
  };

  const getImageAspectRatio = image => {
    if (image.ratio) return image.ratio;
    if (image.width && image.height) return image.width / image.height;
    return 1.33;
  };

  const handleImageError = index =>
    setImageErrors(prev => ({ ...prev, [index]: true }));
  const handleImageLoad = index =>
    setImagesLoaded(prev => ({ ...prev, [index]: true }));
  const handleSlideChange = swiper => setCurrentImageIndex(swiper.activeIndex);

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getTypeIcon = type => {
    switch (type) {
      case 'project':
        return '📁';
      case 'random':
        return '🎲';
      default:
        return '📄';
    }
  };

  const getImageAltText = (project, index = 0) => {
    const title = project.seoTitle || project.title;
    const category = project.category || 'design';
    const type = project.type || 'project';
    if (index === 0) {
      return `${title} - ${category} ${type} portfolio showcase`;
    }
    return `${title} - ${category} ${type} image ${index + 1}`;
  };

  // Render suggestion section
  const renderSuggestionSection = (
    title,
    icon,
    items,
    viewAllLink = '/portfolio',
  ) => {
    if (!items || items.length === 0) return null;

    return (
      <motion.div variants={fadeInUp} className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {icon}
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--text)' }}
            >
              {title}
            </h3>
          </div>
          <Link
            to={viewAllLink}
            className="text-xs hover:underline flex items-center gap-1"
            style={{ color: 'var(--muted)' }}
          >
            View All <FiArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.slice(0, 6).map(item => (
            <div
              key={item.id}
              onClick={() => navigate(`/portfolio/${item.id}`)}
              className="group cursor-pointer rounded-lg overflow-hidden border transition-all hover:shadow-md hover:-translate-y-0.5"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
                <img
                  src={item.thumbnailUrl || item.images?.[0]?.url || ''}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-2">
                <p
                  className="text-xs font-medium truncate"
                  style={{ color: 'var(--text)' }}
                >
                  {item.title}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className="text-[10px] capitalize"
                    style={{ color: 'var(--muted)' }}
                  >
                    {item.category}
                  </span>
                  <span
                    className="text-[10px] flex items-center gap-0.5"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiHeart className="w-2.5 h-2.5 text-red-400" />
                    {item.likes || 0}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  };

  if (loading) return <PortfolioDetailSkeleton />;

  if (error || !project) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="text-center p-8">
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: 'var(--text)' }}
          >
            {error || 'Project not found'}
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
            The project you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm transition-all hover:gap-3"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  const images =
    project.images || (project.imageUrl ? [{ url: project.imageUrl }] : []);
  const imageCount = images.length;
  const shareUrl = `${window.location.origin}/portfolio/${project.id}`;
  const pageTitle = project.seoTitle || project.title;
  const pageDescription = project.seoDescription || project.description;

  const stats = [
    {
      icon: FiHeart,
      label: 'Likes',
      value: project.likes || 0,
      color: 'text-red-500',
    },
    {
      icon: FiEye,
      label: 'Views',
      value: project.views || 0,
      color: 'text-blue-500',
    },
    {
      icon: FiMousePointer,
      label: 'Clicks',
      value: project.clicks || 0,
      color: 'text-green-500',
    },
    {
      icon: FiShare2,
      label: 'Shares',
      value: project.shares || 0,
      color: 'text-purple-500',
    },
  ];

  // Schema markup
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.seoTitle || project.title,
    description: project.seoDescription || project.description,
    keywords: project.tags?.join(', ') || '',
    image: project.thumbnailUrl || images[0]?.url || '',
    dateCreated: project.createdAt,
    dateModified: project.updatedAt || project.createdAt,
    author: { '@type': 'Person', name: 'WD Hajbul' },
    about: { '@type': 'Thing', name: project.category },
    genre: project.type,
  };

  return (
    <>
      {/* 
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/portfolio?action=social-preview&id=xxx
      */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={project.tags?.join(', ') || ''} />
        <link rel="canonical" href={shareUrl} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Sticky Navigation Bar */}
        <div
          className="sticky top-[85px] z-30 backdrop-blur-md border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link
              to="/portfolio"
              className="flex items-center gap-2 text-sm transition-all hover:gap-3"
              style={{ color: 'var(--muted)' }}
            >
              <FiArrowLeft className="w-4 h-4" />
              Back to Portfolio
            </Link>
            <div className="flex items-center gap-3">
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
                  isLiking ? 'opacity-50 cursor-wait' : 'hover:scale-105'
                } ${liked ? 'bg-red-500/20 text-red-500' : ''}`}
                style={{
                  backgroundColor: liked
                    ? 'rgba(239, 68, 68, 0.2)'
                    : 'var(--border)',
                  color: liked ? '#ef4444' : 'var(--text)',
                }}
              >
                {isLiking ? (
                  <div
                    className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: liked ? '#ef4444' : 'var(--text)' }}
                  />
                ) : (
                  <FiHeart
                    className={`w-3.5 h-3.5 ${liked ? 'fill-current' : ''}`}
                  />
                )}
                <span className="text-xs font-medium">{likesCount}</span>
              </button>
              <ShareButtons
                url={shareUrl}
                title={project.title}
                description={project.description}
                imageUrl={project.thumbnailUrl || images[0]?.url || ''}
                project={project}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-8 space-y-6">
              {/* Image Slider */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="relative w-full bg-black/5 dark:bg-white/5 rounded-2xl overflow-hidden"
              >
                <div className="relative aspect-video max-h-[480px]">
                  {imageCount === 0 ? (
                    <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                      <FiImage
                        className="w-12 h-12 mb-3 opacity-30"
                        style={{ color: 'var(--text)' }}
                      />
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        No images available
                      </p>
                    </div>
                  ) : (
                    <>
                      <Swiper
                        modules={[Navigation, Pagination, Zoom]}
                        navigation={{
                          nextEl: '.swiper-button-next',
                          prevEl: '.swiper-button-prev',
                        }}
                        pagination={{ clickable: true, dynamicBullets: true }}
                        zoom={{ maxRatio: 3, minRatio: 1 }}
                        loop={imageCount > 1}
                        onSlideChange={handleSlideChange}
                        className="portfolio-detail-swiper"
                        style={{ width: '100%', height: '100%' }}
                      >
                        {images.map((image, index) => {
                          const imageUrl = image.url || image;
                          const isError = imageErrors[index];
                          const isLoaded = imagesLoaded[index];
                          const ratio = getImageAspectRatio(image);
                          const isPortrait = ratio < 0.9;
                          const altText = getImageAltText(project, index);

                          return (
                            <SwiperSlide key={index}>
                              <div
                                className="w-full h-full flex items-center justify-center bg-black/5 dark:bg-black/20 cursor-zoom-in"
                                onClick={() => {
                                  setPreviewImageIndex(index);
                                  setIsPreviewModalOpen(true);
                                }}
                              >
                                {!isLoaded && !isError && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div
                                      className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin"
                                      style={{ borderColor: 'var(--text)' }}
                                    />
                                  </div>
                                )}
                                {isError ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                                    <FiImage
                                      className="w-12 h-12 mb-3 opacity-30"
                                      style={{ color: 'var(--text)' }}
                                    />
                                    <p
                                      className="text-sm"
                                      style={{ color: 'var(--muted)' }}
                                    >
                                      Unable to load image
                                    </p>
                                  </div>
                                ) : (
                                  <img
                                    src={getOptimizedImageUrl(imageUrl, 800)}
                                    alt={altText}
                                    className={`transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      width: isPortrait ? 'auto' : '100%',
                                      height: isPortrait ? '100%' : 'auto',
                                      objectFit: 'contain',
                                      display: 'block',
                                    }}
                                    onLoad={() => handleImageLoad(index)}
                                    onError={() => handleImageError(index)}
                                    loading="lazy"
                                  />
                                )}
                              </div>
                            </SwiperSlide>
                          );
                        })}
                      </Swiper>

                      {imageCount > 1 && (
                        <>
                          <button
                            className="swiper-button-prev absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full backdrop-blur-md transition-all hover:scale-110 hover:bg-black/70"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.4)',
                              color: 'white',
                            }}
                          >
                            <FiChevronLeft className="w-4 h-4" />
                          </button>
                          <button
                            className="swiper-button-next absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full backdrop-blur-md transition-all hover:scale-110 hover:bg-black/70"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.4)',
                              color: 'white',
                            }}
                          >
                            <FiChevronRight className="w-4 h-4" />
                          </button>
                          <div
                            className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full backdrop-blur-md text-xs font-medium z-10"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                            }}
                          >
                            {currentImageIndex + 1} / {imageCount}
                          </div>
                        </>
                      )}

                      {/* Badges */}
                      <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1.5">
                        {project.featured && (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-yellow-500 text-white flex items-center gap-1 shadow-lg shadow-black/20">
                            <FiStar className="w-3 h-3" /> Featured
                          </span>
                        )}
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-black/70 text-white flex items-center gap-1 backdrop-blur-sm shadow-lg shadow-black/20">
                          {getTypeIcon(project.type)}{' '}
                          {project.type || 'project'}
                        </span>
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-black/70 text-white flex items-center gap-1 backdrop-blur-sm shadow-lg shadow-black/20">
                          <FiTag className="w-3 h-3" /> {project.category}
                        </span>
                      </div>

                      {/* Fullscreen Preview Button */}
                      <button
                        onClick={() => {
                          setPreviewImageIndex(currentImageIndex);
                          setIsPreviewModalOpen(true);
                        }}
                        className="absolute bottom-3 right-3 z-10 p-2 rounded-full backdrop-blur-md transition-all hover:scale-110 hover:bg-black/70"
                        style={{
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                        }}
                      >
                        <FiMaximize2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </motion.div>

              {/* Title & Meta */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <h1
                  className="text-2xl md:text-3xl font-bold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  {project.title}
                </h1>
                <div
                  className="flex flex-wrap gap-3 text-xs"
                  style={{ color: 'var(--muted)' }}
                >
                  <span className="flex items-center gap-1.5">
                    <FiCalendar className="w-3.5 h-3.5" />
                    {formatDate(project.createdAt)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FiTag className="w-3.5 h-3.5" />
                    <span className="capitalize">{project.category}</span>
                  </span>
                  {project.client && project.type !== 'random' && (
                    <span className="flex items-center gap-1.5">
                      <FiUser className="w-3.5 h-3.5" />
                      {project.client}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <FiImage className="w-3.5 h-3.5" />
                    {imageCount} {imageCount === 1 ? 'image' : 'images'}
                  </span>
                </div>
              </motion.div>

              {/* Stats Cards */}
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 sm:grid-cols-4 gap-3"
              >
                {stats.map((stat, idx) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={idx}
                      variants={fadeInUp}
                      className="p-3 rounded-xl border text-center"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <Icon className={`w-4 h-4 mx-auto mb-1 ${stat.color}`} />
                      <p
                        className="text-lg font-bold"
                        style={{ color: 'var(--text)' }}
                      >
                        {stat.value.toLocaleString()}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>
                        {stat.label}
                      </p>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Description */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1 h-6 rounded-full bg-blue-500" />
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Project Description
                  </h2>
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  {formatDescription(project.description)}
                </div>
              </motion.div>

              {/* Tags */}
              {project.tags && project.tags.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1 h-6 rounded-full bg-purple-500" />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      Tags
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-full text-sm transition-all hover:opacity-80"
                        style={{
                          backgroundColor: 'var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Inline Ad */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 text-[10px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span>📢</span>
                  <span>Advertisement</span>
                </div>
                <div
                  className="min-h-[60px] flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    Google Ad Space
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <FiverrCard variant="sidebar" />

              {/* Project Details */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <h3
                  className="text-sm font-semibold mb-3"
                  style={{ color: 'var(--text)' }}
                >
                  <FiFolder className="inline mr-2" /> Project Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Category</span>
                    <span
                      className="capitalize"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.category}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Type</span>
                    <span
                      className="capitalize"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.type}
                    </span>
                  </div>
                  {project.client && project.type !== 'random' && (
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--muted)' }}>Client</span>
                      <span style={{ color: 'var(--text)' }}>
                        {project.client}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Published</span>
                    <span style={{ color: 'var(--text)' }}>
                      {formatDate(project.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Images</span>
                    <span style={{ color: 'var(--text)' }}>{imageCount}</span>
                  </div>
                  {project.featured && (
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--muted)' }}>Status</span>
                      <span className="text-yellow-500 flex items-center gap-1">
                        <FiStar className="w-3 h-3" /> Featured
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border text-center"
                style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--border-light)',
                }}
              >
                <h3
                  className="text-sm font-semibold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  Have a similar project?
                </h3>
                <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
                  Let's collaborate and create something amazing.
                </p>
                <Link
                  to="/hire"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-all hover:gap-3"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  Start a Project <FiArrowRight className="w-3 h-3" />
                </Link>
              </motion.div>

              {/* Sidebar Ad */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 text-[10px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span>📢</span>
                  <span>Advertisement</span>
                </div>
                <div
                  className="min-h-[250px] flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    Google Ad Space
                  </span>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Suggested Projects */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mt-8 pt-8 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 rounded-full bg-gradient-to-b from-blue-500 to-purple-500" />
              <h2
                className="text-xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                Explore More Projects
              </h2>
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                Discover
              </span>
            </div>

            {renderSuggestionSection(
              'Suggested for You',
              <FiTrendingUp className="w-4 h-4 text-blue-500" />,
              suggestedProjects,
            )}
            {renderSuggestionSection(
              'Popular Projects',
              <FiAward className="w-4 h-4 text-yellow-500" />,
              popularProjects,
            )}
            {renderSuggestionSection(
              'You Might Also Like',
              <FiZap className="w-4 h-4 text-purple-500" />,
              forYouProjects,
            )}

            {/* Bottom Ad */}
            <motion.div
              variants={fadeInUp}
              className="p-4 rounded-xl border border-dashed text-center mt-6"
              style={{ borderColor: 'var(--border)' }}
            >
              <div
                className="flex items-center justify-center gap-2 text-[10px]"
                style={{ color: 'var(--muted)' }}
              >
                <span>📢</span>
                <span>Advertisement</span>
              </div>
              <div
                className="min-h-[90px] flex items-center justify-center"
                style={{ backgroundColor: 'var(--border-light)' }}
              >
                <span className="text-xs" style={{ color: 'var(--muted)' }}>
                  Google Ad Space
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Image Preview Modal */}
        <ImagePreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          images={images}
          initialIndex={previewImageIndex}
          project={project}
          onLike={handleLike}
          likesCount={likesCount}
          isLiked={liked}
          isLiking={isLiking}
        />
      </div>

      <style jsx>{`
        .portfolio-detail-swiper {
          width: 100%;
          height: 100%;
        }
        .portfolio-detail-swiper .swiper-button-prev,
        .portfolio-detail-swiper .swiper-button-next {
          display: none;
        }
        .portfolio-detail-swiper .swiper-pagination {
          position: absolute;
          bottom: 4px;
        }
        .portfolio-detail-swiper .swiper-pagination-bullet {
          background: white;
          opacity: 0.5;
          width: 6px;
          height: 6px;
        }
        .portfolio-detail-swiper .swiper-pagination-bullet-active {
          opacity: 1;
          width: 16px;
          border-radius: 4px;
        }
        .portfolio-detail-swiper .swiper-zoom-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .portfolio-detail-swiper .swiper-slide {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
        }
        .portfolio-detail-swiper .swiper-wrapper {
          height: 100%;
        }
        .prose {
          color: var(--text);
        }
        .prose p {
          margin-bottom: 0.75rem;
          line-height: 1.7;
        }
        .prose ul,
        .prose ol {
          margin: 0.5rem 0 1rem 0;
          padding-left: 1.25rem;
        }
        .prose li {
          margin-bottom: 0.25rem;
          line-height: 1.6;
        }
        .prose h3 {
          font-size: 1.125rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .prose h4 {
          font-size: 1rem;
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
      `}</style>
    </>
  );
}

export default PortfolioDetail;
